import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ProductStockBadge } from "$components";
import useStyles from "./styles/Catalogues.styles";
import { EntreprisesServices } from "$services";
import useReferencingCategory from "$hooks/useReferencingCategory";
import useAuth from "$hooks/useAuth";
import { Product } from "$containers";
import { usePopin } from "$components/Popin/usePopin";
import { CatalogueProductForm } from "./CatalogueProductForm";

import { DataTableWithContext, EnumDataRowType } from "$components/DataTable";
import { getProductStock } from "$services/Products.service";
import { ActionButtons } from "./ActionButtons";
import { useQueryParameters } from "$hooks/useQueryParameters";
import { useDebounce } from "$utils/customHooks";
import { CacheInvalidationAction } from "$utils/cache";

export function CategoryTableProduct({
  companyId,
  catalogWMS,
  setSelectedDeleteProduct,
  setOpenModalDeleteProduct,
  filterText,
  filterByCateg,
  filterIsActive,
}: any) {
  const token = localStorage.getItem("access_token");
  const classes = useStyles();
  const params = useQueryParameters();

  const [selectedProduct, setSelectedProduct] = useState<any>(undefined);

  const [product, setProduct] = useState(null);

  const { data: company } = useQuery({
    queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, companyId],
    queryFn: () => EntreprisesServices.FetchCompany(token, companyId),
    enabled: !!companyId,
    staleTime: 1000 * 60,
  });

  const { isAdmin } = useAuth();
  const { Popin, PopinProps, togglePopin } = usePopin();

  // truc inutile à dégager à terme
  const { referencingCategory, loading: isLoadingRefCategory } =
    useReferencingCategory(companyId);

  const headerData = [
    {
      id: "designation",
      label: "designation",
      cell: {
        onClick: (p) => setProduct(p),
        type: EnumDataRowType.EmptyLink,
        value: ({ designation, reference }) => {
          return {
            title: designation,
            subtitle: `Ref: ${reference}`,
            isDisabled: !filterIsActive,
          };
        },
        isDisabled: !filterIsActive,
      },
    },
    {
      id: "unit",
      label: "unit",
      cell: {
        value: ({ unit }) => unit,
        isDisabled: !filterIsActive,
      },
    },
    {
      id: "quantity",
      label: "quantity",
      width: 150,
      cell: {
        value: ({ _id }) => (
          <ProductStockBadge
            productId={_id}
            companyId={companyId}
            isDisabled={!filterIsActive}
          />
        ),
      },
    },
    {
      id: "actions",
      label: "actions",
      width: 100,
      cell: {
        value: (product) => (
          <ActionButtons
            product={product}
            togglePopin={togglePopin}
            catalogWMS={catalogWMS}
            setSelectedProduct={setSelectedProduct}
            setSelectedDeleteProduct={setSelectedDeleteProduct}
            setOpenModalDeleteProduct={setOpenModalDeleteProduct}
            referencingCategory={referencingCategory}
            isLoadingRefCategory={isLoadingRefCategory}
            company={company}
          />
        ),
      },
    },
  ];

  const debouncedFilterText = useDebounce(filterText);

  const externalState = useMemo(() => {
    const returnState = {};

    for (const [key, value] of params.entries()) {
      returnState[key] = value;
    }

    const {
      page,
      rowsPerPage,
      warehouseId: _warehouseId,
      ...filters
    } = returnState as any;
    filters.category = filterByCateg;
    filters.$orSearch = debouncedFilterText;
    filters.isActive = filterIsActive;

    return {
      filters: { ...filters, company: companyId },
      pagination: { page, rowsPerPage },
    } as any;
  }, [params, companyId, filterByCateg, debouncedFilterText, filterIsActive]);

  return (
    <>
      <DataTableWithContext
        externalState={externalState}
        className={classes.tableContainer}
        rowsPerPage={10}
        thead={headerData}
        queryFunction={getProductStock}
        queryFunctionName={CacheInvalidationAction.GET_PRODUCT_STOCK}
        data-testid={`productStock_${companyId}`}
      />

      <Product
        noCart
        companyId={companyId}
        product={product}
        resetProduct={() => setProduct(undefined)}
      />
      <Popin {...PopinProps}>
        {isAdmin() && (
          <CatalogueProductForm
            togglePopin={togglePopin}
            companyId={companyId}
            product={selectedProduct}
            setSelectedProduct={setSelectedProduct}
          />
        )}
      </Popin>
    </>
  );
}
